import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Theme,
  ThemeProvider,
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import SupervisedUserCircle from "@material-ui/icons/SupervisedUserCircle";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import { DataProviderContext, useAuthProvider } from "react-admin";
import { AuthManager } from "../../common/auth/Auth";
import { MultiTenancySides } from "../../common/data/service-proxies/api";
import {
  useServerMaintenanceState,
  closeServerMaintenanceDialog,
} from "../../common/store/serverMaintenanceStore";
import { SwitchToCustomerForm } from "./SwitchToCustomerForm";
import { stopPropagationForTab } from "../../common/util/stopPropagationForTab";
import { useProLogsTheme } from "../../common/hooks/useProLogsTheme";

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  icon: {
    color: theme.palette.warning.main,
  },
  message: {
    whiteSpace: "pre-wrap",
  },
}));

interface ServerMaintenanceDialogProps {
  authProvider?: AuthManager;
  dataProvider?: any;
}

const MAINTENANCE_LOGOUT_URL = "/logout?reason=server-maintenance";

export const ServerMaintenanceDialog: React.FC<ServerMaintenanceDialogProps> = (
  props
) => {
  const classes = useStyles();
  const theme = useProLogsTheme();
  const { isOpen, message } = useServerMaintenanceState();
  const authFromContext = useAuthProvider() as AuthManager | undefined;
  const authProvider = props.authProvider ?? authFromContext;
  const dataProvider = props.dataProvider;
  const isOverridden = authProvider?.isUserOverride?.() ?? false;
  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  const [identity, setIdentity] = useState<{ multitenancy_side?: number | string } | null>(null);

  useEffect(() => {
    if (!isOpen || !authProvider?.getIdentity) return;
    authProvider.getIdentity().then((id) => setIdentity(id as { multitenancy_side?: number | string })).catch(() => setIdentity(null));
  }, [isOpen, authProvider]);

  const multitenancySide = identity?.multitenancy_side;
  const isHost =
    multitenancySide !== undefined &&
    String(multitenancySide) === String(MultiTenancySides.Host);

  const showViewAsCustomer = isHost || isOverridden;
  const showViewAsMasterAdmin = isOverridden;

  const handleViewAsCustomer = useCallback(() => {
    setSwitchDialogOpen(true);
  }, []);

  const handleViewAsMasterAdmin = useCallback(() => {
    const promise = authProvider?.setUserOverride?.(undefined);
    if (promise && typeof promise.then === "function") {
      promise
        .then(() => {
          closeServerMaintenanceDialog();
          window.location.href = "/";
        })
        .catch(() => { });
    } else {
      closeServerMaintenanceDialog();
      window.location.href = "/";
    }
  }, [authProvider]);

  const handleSwitchSuccess = useCallback(() => {
    setSwitchDialogOpen(false);
    closeServerMaintenanceDialog();
    window.location.href = "/";
  }, []);

  const handleSwitchFailed = useCallback(() => {
    setSwitchDialogOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    closeServerMaintenanceDialog();
    // `logout()` hands back a bare "/logout" for a signed-in user, which would
    // land here as a plain click. The dialog knows why it is signing the user
    // out, so it always says so.
    Promise.resolve(authProvider?.logout?.({ redirectBack: true }))
      .catch(() => undefined)
      .then(() => {
        window.location.href = MAINTENANCE_LOGOUT_URL;
      });
  }, [authProvider]);

  const handleCloseSwitchDialog = useCallback(() => {
    setSwitchDialogOpen(false);
  }, []);

  const hasSwitchOptions = showViewAsCustomer || showViewAsMasterAdmin;

  return (
    <>
      <Dialog
        open={isOpen}
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        onKeyDown={stopPropagationForTab}
      >
        <DialogTitle>
          <span className={classes.title}>
            <WarningIcon className={classes.icon} fontSize="large" />
            Server Maintenance
          </span>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <p className={classes.message}>{message || "The server is undergoing maintenance. Please try again later."}</p>
        </DialogContent>
        <Divider />
        <DialogActions>
          {hasSwitchOptions && (
            <>
              {showViewAsCustomer && (
                <Button
                  color="primary"
                  startIcon={<SupervisedUserCircle />}
                  onClick={handleViewAsCustomer}
                >
                  View as customer...
                </Button>
              )}
              {showViewAsMasterAdmin && (
                <Button
                  color="primary"
                  startIcon={<SupervisedUserCircle />}
                  onClick={handleViewAsMasterAdmin}
                >
                  View as master admin
                </Button>
              )}
            </>
          )}
          {!hasSwitchOptions && (
            <Button
              color="primary"
              startIcon={<ExitToAppIcon />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={switchDialogOpen}
        onClose={handleCloseSwitchDialog}
        maxWidth="sm"
        fullWidth
        onKeyDown={stopPropagationForTab}
      >
        <ThemeProvider theme={theme}>
          <DialogTitle>View as customer</DialogTitle>
          <Divider />
          {dataProvider ? (
            <DataProviderContext.Provider value={dataProvider}>
              <SwitchToCustomerForm
                authProvider={authProvider}
                isOverridden={false}
                onSuccess={handleSwitchSuccess}
                onFailed={handleSwitchFailed}
              />
            </DataProviderContext.Provider>
          ) : (
            <SwitchToCustomerForm
              authProvider={authProvider}
              isOverridden={false}
              onSuccess={handleSwitchSuccess}
              onFailed={handleSwitchFailed}
            />
          )}
        </ThemeProvider>
      </Dialog>
    </>
  );
};
